"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { buildVideoPlayerInputs } from "@/components/VideoPlayer";
import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

const ARTICLE_RESTRICTED_CONTENT_BINDINGS = {
  "component.options.articleType": "state.article.articleType",
  "component.options.controlColor": "state.article.controlColor",
  "component.options.articleId": "state.article.articleId",
  "component.options.title": "state.article.title",
  "component.options.subtitle": "state.article.subtitle",
  "component.options.creationDate": "state.article.creationDate",
  "component.options.image.altText": "state.article.image.altText",
  "component.options.image.smallReference": "state.article.image.smallReference",
  "component.options.image.mediumReference": "state.article.image.mediumReference",
  "component.options.image.largeReference": "state.article.image.largeReference",
} as const;

export const articleRestrictedContentInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Article 1",
  },
  {
    name: "subtitle",
    type: "richText",
    required: true,
    defaultValue: "Article 1 subtitle",
  },
  {
    name: "creationDate",
    type: "date",
    required: true,
    helperText: "The creation date of the blog post",
    defaultValue: "2021-08-05T00:00:00.000Z",
  },
  {
    name: "articleType",
    type: "string",
    enum: ["article", "video", "quiz"],
    defaultValue: "article",
  },
  {
    name: "controlColor",
    friendlyName: "Control color",
    type: "string",
    enum: ["dark", "light"],
    defaultValue: "dark",
    showIf: `options.get("articleType") === "article" || options.get("articleType") === "quiz"`,
    helperText: "Controls the Earn points / Points earned ribbon text color for this article.",
  },
  {
    name: "percentageToWatch",
    type: "string",
    showIf: `options.get("articleType") === "video"`,
    regex: {
      pattern: "^[1-9][0-9]?$|^100$",
      options: "g",
      message: "You must use number between 1-100",
    },
  },
  {
    name: "articleId",
    type: "string",
    required: true,
    helperText: "Id corresponding to the id of article set in Open Loyalty",
  },
  {
    name: "disableTiers",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "image",
    type: "object",
    required: true,
    showIf: `options.get("articleType") !== "video" && options.get("headerMediaType") !== "video"`,
    defaultValue: {
      smallReference:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      mediumReference:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      largeReference:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      altText: "article",
    },
    subFields: [
      {
        name: "smallReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Small version of the image",
        defaultValue: "",
      },
      {
        name: "mediumReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Medium version of the image",
      },
      {
        name: "largeReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Large version of the image",
      },
      {
        name: "altText",
        type: "string",
        required: true,
        helperText: "Alternative text for the image",
      },
    ],
  },
  {
    name: "headerMediaType",
    type: "string",
    enum: ["image", "video"],
    friendlyName: "Header media type",
    helperText: "Choose the media displayed in the article header. Defaults to image when not set.",
    showIf: `options.get("articleType") === "article" || options.get("articleType") === "quiz"`,
  },
  {
    name: "video",
    type: "object",
    showIf: `options.get("articleType") === "video" || options.get("headerMediaType") === "video"`,
    subFields: buildVideoPlayerInputs(),
  },
  {
    name: "quizBannerContent",
    type: "object",
    friendlyName: "Quiz banner",
    helperText: "Configurable copy for the quiz banners. Use {points} as a placeholder for the point value.",
    showIf: `options.get("articleType") === "quiz"`,
    defaultValue: {
      topBannerPointsLabel: "POINTS",
      topBannerTitle: "Read the article, and complete the quiz to earn points.",
      topBannerCta: "Start quiz",
      bottomBannerTitle: "Ready to <b>earn {points} points</b>? Complete this quiz.",
      bottomBannerEarnedTitle: "You earned <b>{points} points</b> for this quiz.",
      bottomBannerCta: "Start quiz",
      bottomBannerRedeemCta: "Redeem points",
      bottomBannerRedeemUrl: "#",
    },
    subFields: [
      {
        name: "topBannerPointsLabel",
        type: "string",
        required: true,
        friendlyName: "Top banner — points label",
        helperText: "Label under the point value in the top banner",
      },
      {
        name: "topBannerTitle",
        type: "string",
        required: true,
        friendlyName: "Top banner — title",
      },
      {
        name: "topBannerCta",
        type: "string",
        required: true,
        friendlyName: "Top banner — CTA",
      },
      {
        name: "bottomBannerTitle",
        type: "string",
        required: true,
        friendlyName: "Bottom banner — title",
        helperText: "Supports {points} placeholder and basic HTML",
      },
      {
        name: "bottomBannerEarnedTitle",
        type: "string",
        required: true,
        friendlyName: "Bottom banner — title (points earned)",
        helperText: "Supports {points} placeholder and basic HTML",
      },
      {
        name: "bottomBannerCta",
        type: "string",
        required: true,
        friendlyName: "Bottom banner — CTA",
      },
      {
        name: "bottomBannerRedeemCta",
        type: "string",
        required: true,
        friendlyName: "Bottom banner — redeem CTA (points earned)",
      },
      {
        name: "bottomBannerRedeemUrl",
        type: "url",
        required: true,
        friendlyName: "Bottom banner — redeem URL (points earned)",
      },
    ],
  },
  {
    name: "quizContent",
    type: "object",
    friendlyName: "Quiz content",
    helperText: "Questions and screen configuration for the quiz pop-up.",
    showIf: `options.get("articleType") === "quiz"`,
    defaultValue: {
      progressTemplate: "{current} OF {total}",
      successToastMessage: "You've earned {points} points!",
      errorToastMessage: "Something went wrong while earning points.",
      alreadyCompletedMessage: "Something went wrong while earning points.",
      notAvailableMessage: "Something went wrong while earning points.",
      completeScreen: {
        title: "Thank you for participating!",
        description: "Here's a little something to thank you for completing the quiz.",
        pointsLabel: "You earned {points} points",
        defaultCtaLabel: "Close",
      },
      exitConfirmation: {
        title: "Leaving already?",
        description: "If you leave now, you won't earn any points for this quiz.",
        dropQuizLabel: "Drop quiz",
        stayLabel: "Stay",
      },
    },
    subFields: [
      {
        name: "accessibilityDialogLabel",
        type: "string",
        friendlyName: "Accessibility dialog label",
        helperText: "Accessible label for the quiz modal",
      },
      {
        name: "progressTemplate",
        type: "string",
        required: true,
        friendlyName: "Progress template",
        helperText:
          'Template for the question progress indicator. Use {current} and {total} as placeholders, e.g. "{current} OF {total}"',
      },
      {
        name: "successToastMessage",
        type: "string",
        required: true,
        friendlyName: "Success toast message",
        helperText: "Message displayed when the user earns points. Supports {points} placeholder for points.",
      },
      {
        name: "errorToastMessage",
        type: "string",
        required: true,
        friendlyName: "Error toast message",
        helperText: "Message displayed when there is an error earning points.",
      },
      {
        name: "alreadyCompletedMessage",
        type: "string",
        required: true,
        friendlyName: "Error toast message when quiz already completed",
        helperText: "Shown when the consumer has already completed this quiz.",
      },
      {
        name: "notAvailableMessage",
        type: "string",
        required: true,
        friendlyName: "Error toast message when quiz not available for consumer",
        helperText: "Shown when the quiz is not available for this consumer.",
      },
      {
        name: "questions",
        type: "list",
        required: true,
        friendlyName: "Questions (exactly 3 required)",
        helperText: "Exactly 3 questions must be configured. Each question requires at least 2 answer options.",
        onChange: (options: Map<string, unknown>) => {
          const items = options.get("questions");
          if (Array.isArray(items) && items.length > 3) {
            options.set("questions", items.slice(0, 3));
            alert("A quiz requires exactly 3 questions. Extra questions have been removed.");
          }
        },
        subFields: [
          {
            name: "title",
            type: "string",
            required: true,
            friendlyName: "Question title",
          },
          {
            name: "description",
            type: "string",
            friendlyName: "Question description (optional)",
          },
          {
            name: "correctFeedback",
            type: "string",
            required: true,
            friendlyName: "Correct answer feedback",
          },
          {
            name: "incorrectFeedback",
            type: "string",
            required: true,
            friendlyName: "Incorrect answer feedback",
          },
          {
            name: "nextButtonLabel",
            type: "string",
            required: true,
            friendlyName: "Next button label",
          },
          {
            name: "options",
            type: "list",
            required: true,
            friendlyName: "Answer options (2–4)",
            helperText: "At least 2 and at most 4 answer options. Exactly one must be marked as correct.",
            onChange: (options: Map<string, unknown>) => {
              const items = options.get("options");
              if (Array.isArray(items)) {
                if (items.length > 4) {
                  options.set("options", items.slice(0, 4));
                  alert("Maximum 4 answer options are allowed. Extra options have been removed.");
                }
              }
            },
            subFields: [
              {
                name: "text",
                type: "string",
                required: true,
                friendlyName: "Answer text",
              },
              {
                name: "isCorrect",
                type: "boolean",
                required: true,
                defaultValue: false,
                friendlyName: "Is correct answer",
              },
            ],
          },
        ],
      },
      {
        name: "completeScreen",
        type: "object",
        required: true,
        friendlyName: "Completion screen",
        helperText: "Content shown after the user successfully completes the quiz.",
        subFields: [
          {
            name: "title",
            type: "string",
            required: true,
            friendlyName: "Title",
          },
          {
            name: "description",
            type: "string",
            required: true,
            friendlyName: "Description",
          },
          {
            name: "pointsLabel",
            type: "string",
            required: true,
            friendlyName: "Points label",
          },
          {
            name: "defaultCtaLabel",
            type: "string",
            required: true,
            friendlyName: "Default CTA label",
            helperText: "Used when no custom CTAs are configured",
          },
          {
            name: "ctas",
            type: "list",
            friendlyName: "Custom CTAs (max 2)",
            helperText: "Up to 2 configurable CTA buttons. If empty, a single default Close button is shown.",
            subFields: [
              {
                name: "label",
                type: "string",
                required: true,
                friendlyName: "CTA label",
              },
              {
                name: "url",
                type: "url",
                required: true,
                friendlyName: "CTA URL",
              },
            ],
          },
        ],
      },
      {
        name: "exitConfirmation",
        type: "object",
        required: true,
        friendlyName: "Exit confirmation",
        helperText: "Content shown when the user tries to close the quiz before completing it.",
        subFields: [
          {
            name: "title",
            type: "string",
            required: true,
            friendlyName: "Title",
          },
          {
            name: "description",
            type: "string",
            required: true,
            friendlyName: "Description",
          },
          {
            name: "dropQuizLabel",
            type: "string",
            required: true,
            friendlyName: "Drop quiz button label",
          },
          {
            name: "stayLabel",
            type: "string",
            required: true,
            friendlyName: "Stay button label",
          },
        ],
      },
    ],
  },
  {
    name: "restrictImage",
    type: "object",
    required: false,
    defaultValue: {
      reference:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      altText: "article",
    },
    subFields: [
      {
        name: "smallReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Small version of the image",
        defaultValue:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      },
      {
        name: "mediumReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Medium version of the image",
        defaultValue:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      },
      {
        name: "largeReference",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
        helperText: "Large version of the image",
        defaultValue:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
      },
      {
        name: "altText",
        type: "string",
        required: true,
        helperText: "Alternative text for the image",
      },
    ],
  },
  {
    name: "primaryBtnUrl",
    type: "url",
    required: true,
    defaultValue: "#",
    friendlyName: "The URL of the primary button",
  },
  {
    name: "secondaryBtnUrl",
    type: "url",
    required: true,
    defaultValue: "#",
    friendlyName: "The URL of the secondary button",
  },
  {
    name: "primaryBtnUrlRestricted",
    type: "url",
    required: true,
    defaultValue: "#",
    friendlyName: "The URL of the primary button when restricted by tier",
  },
  {
    name: "previewContent",
    type: "richText",
    required: false,
    defaultValue: "#",
    friendlyName: "Preview content for restricted users",
  },
  {
    name: "relatedArticlesTitle",
    type: "string",
    helperText: "The title of the blog related Articles",
    defaultValue: "Related Articles",
  },
  {
    name: "relatedArticlesCtaLabel",
    type: "string",
    helperText: "The label of the cta",
    defaultValue: "View all products",
  },
  {
    name: "relatedArticlesCtaUrl",
    type: "string",
    helperText: "The url of the cta",
    defaultValue: "/products",
  },
  {
    name: "relatedArticles",
    type: "list",
    subFields: [
      {
        name: "article",
        type: "reference",
        model: "loyalty-blog-single-article-section-model",
      },
    ],
  },
] as const satisfies Inputs;

export const ArticleRestrictedContentComponent: RegisteredComponent = {
  name: "ArticleRestrictedContent",
  component: dynamic(async () =>
    withValidation(
      articleRestrictedContentInputs,
      (await import("./ArticleRestrictedContent")).ArticleRestrictedContent,
      "ArticleRestrictedContent"
    )
  ),
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  inputs: articleRestrictedContentInputs,
  defaults: {
    bindings: ARTICLE_RESTRICTED_CONTENT_BINDINGS,
  },
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'ArticleRestrictedContent' component can only be added inside a 'LoyaltyWrapper'",
  },
};
